/**
 * Meus textos — a lista do editor de texto livre.
 *
 * É a porta de entrada do "Docs" do site: criar um texto novo, abrir o que já
 * existe, apresentar (ler em corpo grande) sem baixar nada, duplicar ou apagar.
 *
 * A tela é de propósito simples: quem chega aqui muitas vezes nunca usou um
 * editor online, e o que ele precisa ver primeiro é **um botão grande de criar**.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Loader2, PenLine, Plus, Presentation, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Campo } from '@/components/ui/label';
import { api, ErroApi, type TextoCompartilhado, type TextoSalvo } from '@/lib/api';
import { ModelosDeTexto } from '@/texto/ModelosDeTexto';

export function Textos() {
  const navegar = useNavigate();
  const [textos, setTextos] = useState<TextoSalvo[] | null>(null);
  const [compartilhados, setCompartilhados] = useState<TextoCompartilhado[]>([]);
  const [titulo, setTitulo] = useState('');
  const [erro, setErro] = useState('');
  /** A conta não tem o módulo liberado (área exclusiva, por convite). */
  const [semModulo, setSemModulo] = useState(false);
  const [ocupado, setOcupado] = useState('');

  const carregar = useCallback(async () => {
    try {
      const lista = await api.listarTextos();
      setTextos(lista.textos);
      setCompartilhados(lista.compartilhados ?? []);
      setSemModulo(false);
      setErro('');
    } catch (causa) {
      if (causa instanceof ErroApi && causa.status === 403) {
        setSemModulo(true);
        setTextos([]);
        setCompartilhados([]);
        setErro('');
        return;
      }
      setTextos([]);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar seus textos.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function criar() {
    const nome = titulo.trim() || 'Texto sem título';
    setOcupado('criar');
    try {
      const { texto } = await api.criarTexto({ titulo: nome });
      navegar(`/textos/${texto.id}`);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível criar o texto.');
      setOcupado('');
    }
  }

  async function duplicar(id: string) {
    setOcupado(id);
    try {
      await api.duplicarTexto(id);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível duplicar.');
    } finally {
      setOcupado('');
    }
  }

  async function apagar(id: string, nome: string) {
    if (!window.confirm(`Apagar "${nome}"? O texto não volta.`)) return;
    setOcupado(id);
    try {
      await api.apagarTexto(id);
      await carregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível apagar.');
    } finally {
      setOcupado('');
    }
  }

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <PenLine className="size-5" />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight">Meus textos</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Um editor de texto simples, para o que não é trabalho acadêmico: carta, ata, aula, receita,
          roteiro, memorial. Escreva, escolha a fonte, baixe em <strong>.docx</strong> ou imprima em{' '}
          <strong>PDF</strong> — e, para ler depois, use o botão <strong>Apresentar</strong>, que mostra o
          texto em letras grandes, sem baixar nada.
        </p>
      </header>

      {semModulo ? (
        <Card>
          <CardHeader>
            <CardTitle>Área exclusiva, por convite</CardTitle>
            <CardDescription>
              O editor de textos é um módulo separado do resto da plataforma, liberado conta por conta. Sua
              conta ainda não tem esse acesso — fale com quem te atende para receber o convite.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="contorno">
              <a href="/contato">Falar com o desenvolvedor</a>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {erro ? (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {erro}
            </p>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Começar um texto</CardTitle>
              <CardDescription>Dê um nome agora — dá para mudar depois, dentro do editor.</CardDescription>
            </CardHeader>
            <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <Campo rotulo="Nome do texto" dica="É só para você achar depois.">
                <input
                  value={titulo}
                  onChange={(evento) => setTitulo(evento.target.value)}
                  placeholder="Ata da reunião de outubro"
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9"
                />
              </Campo>
            </div>
            <Button onClick={() => void criar()} disabled={ocupado === 'criar'}>
              {ocupado === 'criar' ? <Loader2 className="animate-spin" /> : <Plus />}
              Criar texto
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Os modelos prontos: o caminho mais rápido para quem já sabe o que quer. */}
      <ModelosDeTexto aoCriar={carregar} />

      <section className="space-y-4">
        <h2 className="text-lg font-semibold tracking-tight">Guardados</h2>

        {textos === null ? (
          <div className="flex min-h-[20vh] items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-sm">Carregando...</span>
          </div>
        ) : textos.length === 0 ? (
          <Card>
            <CardHeader>
              <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                <FileText className="size-5" />
              </span>
              <CardTitle className="pt-1">Nenhum texto ainda</CardTitle>
              <CardDescription>
                Escreva o primeiro acima. Ele fica guardado na sua conta e abre em qualquer aparelho.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2">
            {textos.map((texto) => (
              <li key={texto.id}>
                <Card className="flex h-full flex-col">
                  <CardHeader>
                    <CardTitle className="text-base leading-snug">{texto.titulo}</CardTitle>
                    <CardDescription>
                      {texto.caracteres.toLocaleString('pt-BR')} caractere(s) · atualizado em{' '}
                      {new Date(texto.atualizadoEm).toLocaleDateString('pt-BR')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto space-y-3">
                    <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                      {texto.previa || 'Texto vazio.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild tamanho="pequeno">
                        <Link to={`/textos/${texto.id}`}>
                          <PenLine />
                          Escrever
                        </Link>
                      </Button>
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <Link to={`/textos/${texto.id}/apresentar`}>
                          <Presentation />
                          Apresentar
                        </Link>
                      </Button>
                      <Button
                        variant="fantasma"
                        tamanho="pequeno"
                        onClick={() => void duplicar(texto.id)}
                        disabled={ocupado === texto.id}
                      >
                        Duplicar
                      </Button>
                      <Button
                        variant="fantasma"
                        tamanho="pequeno"
                        onClick={() => void apagar(texto.id, texto.titulo)}
                        disabled={ocupado === texto.id}
                      >
                        <Trash2 />
                        Apagar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* O que outras contas compartilharam comigo — só aparece quando há. */}
      {compartilhados.length > 0 ? (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold tracking-tight">Compartilhados comigo</h2>
          <ul className="grid gap-4 md:grid-cols-2">
            {compartilhados.map((texto) => (
              <li key={texto.id}>
                <Card className="flex h-full flex-col">
                  <CardHeader>
                    <CardTitle className="text-base leading-snug">{texto.titulo}</CardTitle>
                    <CardDescription>
                      De {texto.dono} · {texto.podeEditar ? 'pode editar' : 'só leitura'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto space-y-3">
                    <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                      {texto.previa || 'Texto vazio.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button asChild tamanho="pequeno">
                        <Link to={`/textos/${texto.id}`}>
                          <PenLine />
                          {texto.podeEditar ? 'Escrever' : 'Ler'}
                        </Link>
                      </Button>
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <Link to={`/textos/${texto.id}/apresentar`}>
                          <Presentation />
                          Apresentar
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
        </>
      )}
    </div>
  );
}
